/**
 * @fileoverview Hook for browser geolocation
 * @description Detects user location and converts to US zipcode via OpenStreetMap
 */

import { useState, useEffect, useCallback } from 'react'
import type { GeolocationState } from './types'

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse'

interface NominatimResponse {
  address?: {
    postcode?: string
    country_code?: string
  }
}

export const useGeolocation = (autoDetect: boolean = true) => {
  const [state, setState] = useState<GeolocationState>({
    loading: autoDetect,
    zipcode: null,
    error: null,
    isUSA: false,
  })

  const fetchZipcode = useCallback(async (latitude: number, longitude: number) => {
    try {
      const response = await fetch(
        `${NOMINATIM_URL}?lat=${latitude}&lon=${longitude}&format=json`,
        { headers: { 'Accept-Language': 'en' } }
      )

      if (!response.ok) throw new Error('Failed to fetch location')

      const data: NominatimResponse = await response.json()
      const countryCode = data.address?.country_code?.toLowerCase()
      const isUSA = countryCode === 'us'

      setState({
        loading: false,
        zipcode: isUSA ? data.address?.postcode || null : null,
        error: null,
        isUSA,
      })
    } catch {
      setState({
        loading: false,
        zipcode: null,
        error: 'Failed to detect location',
        isUSA: false,
      })
    }
  }, [])

  const detectLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState({
        loading: false,
        zipcode: null,
        error: 'Geolocation not supported',
        isUSA: false,
      })
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchZipcode(position.coords.latitude, position.coords.longitude)
      },
      (error) => {
        setState({
          loading: false,
          zipcode: null,
          error: error.code === 1 ? 'Location permission denied' : 'Failed to get location',
          isUSA: false,
        })
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    )
  }, [fetchZipcode])

  useEffect(() => {
    if (autoDetect) {
      detectLocation()
    }
  }, [autoDetect, detectLocation])

  return { ...state, retry: detectLocation }
}
